'use client';

/**
 * 루트 레이아웃 붕괴 대비 전역 에러 (App Router global-error.tsx 규약)
 *
 * Next.js App Router: global-error.tsx 는 루트 layout.tsx 자체의 렌더가 실패할 때
 * 등 최상위에서 발생한 예외를 잡는 최후의 폴백이다. 이 시점에는 루트 레이아웃이
 * 렌더되지 않으므로 자체 <html>/<body>를 직접 렌더해야 한다. 'use client' 필수.
 *
 * 주의: 이 화면은 전역 CSS(토큰)·웹폰트가 로드되지 않았을 수 있는 최악의 상황을
 * 가정한다. 따라서 CSS Module/토큰에 의존하지 않고 인라인 스타일 + 인라인 <button>을
 * 사용한다(스펙이 허용한 유일한 하드코딩 예외). Government Blue(#2563EB) 등 값은
 * styles/variables.css의 대응 토큰과 일치한다.
 */

import { useEffect } from 'react';

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

// 폰트 미로드 대비: 시스템 한글 폰트 폴백까지 명시.
const FONT_STACK =
  "'Pretendard Variable', Pretendard, -apple-system, BlinkMacSystemFont, 'Malgun Gothic', '맑은 고딕', system-ui, sans-serif";

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console -- 개발 모드 한정 루트 붕괴 로깅
      console.error('[global-error.tsx] 루트 레이아웃 에러:', error);
    }
    // 프로덕션 리포팅 훅 자리: 외부 에러 추적 서비스로 전송 가능.
  }, [error]);

  return (
    <html lang="ko">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          backgroundColor: '#FAFAFA',
          color: '#262626',
          fontFamily: FONT_STACK,
          lineHeight: 1.6,
          WebkitFontSmoothing: 'antialiased',
        }}
      >
        <main
          style={{
            width: '100%',
            maxWidth: '460px',
            padding: '56px 32px',
            textAlign: 'center',
            backgroundColor: '#FFFFFF',
            border: '1px solid #E8E8E8',
            borderRadius: '24px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.03)',
          }}
        >
          {/* 아이콘 (인라인 SVG — 삼각 경고) */}
          <div
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '72px',
              height: '72px',
              marginBottom: '24px',
              borderRadius: '9999px',
              backgroundColor: 'rgba(37, 99, 235, 0.1)',
              color: '#2563EB',
            }}
          >
            <svg
              width="34"
              height="34"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>

          <h1
            style={{
              margin: '0 0 8px',
              fontSize: '28px',
              fontWeight: 700,
              letterSpacing: '-0.01em',
            }}
          >
            일시적인 문제가 발생했습니다
          </h1>
          <p
            style={{
              margin: '0 0 32px',
              fontSize: '16px',
              color: '#475569',
            }}
          >
            화면을 불러오는 중 문제가 발생했습니다. 아래 버튼을 눌러 새로고침해 주세요. 문제가
            계속되면 잠시 후 다시 접속해 주세요.
          </p>

          <button
            type="button"
            onClick={() => reset()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              minHeight: '48px',
              padding: '0 24px',
              fontSize: '16px',
              fontWeight: 500,
              fontFamily: 'inherit',
              color: '#FFFFFF',
              backgroundColor: '#2563EB',
              border: '1px solid transparent',
              borderRadius: '10px',
              cursor: 'pointer',
            }}
          >
            <svg
              aria-hidden="true"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 2v6h6" />
              <path d="M3 13a9 9 0 1 0 3-7.7L3 8" />
            </svg>
            새로고침
          </button>
        </main>
      </body>
    </html>
  );
}
